import {ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ScreenshotId, ImageUrl, LocalScreenshotId} from '../../types/ids';
import {ModalToolContainerService} from '../../tools/modaltoolcontainer/modaltoolcontainer.service';
import {
    ScreenshotViewerComponent,
    ScreenshotViewerToolConfig
} from '../../tools/screenshotviewer/screenshotviewer.component';
import {ImageDownloadRequest, ImageService} from '@webmate-web-core/shared/services/image.service';
import {faImage} from "@fortawesome/pro-solid-svg-icons";
import {Observable, of, Subscription} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

/**
 * Simple component to display screenshot from the backend using either ScreenshotId or ImageUrl, without overlays.
 * To display overlays or in memory screenshots from a ScreenshotDataUrl, use wm-canvas-based-image
 */
@UntilDestroy()
@Component({
    selector: 'wm-img-tag-based-image',
    templateUrl: './img-tag-based-image.component.html',
    styleUrls: ['./img-tag-based-image.component.less']
})
export class ImgTagBasedImageComponent implements OnInit {
    constructor(private imageService: ImageService, private cdRef: ChangeDetectorRef) {}

    @Input()
    downloadable: boolean;

    @Input()
    imageId?: ScreenshotId;

    @Input()
    small?: boolean = false;

    @Input()
    imageUrl?: ImageUrl;

    @Input()
    fillContainer: boolean = false;

    isLoaded = false;

    isError = false;

    errorText = "Error loading image";

    ngOnChanges(changes: SimpleChanges) {
        const imageHasChanged = (changes.imageId && changes.imageId.previousValue !== changes.imageId.currentValue) ||
            changes.imageUrl && changes.imageUrl.previousValue !== changes.imageUrl.currentValue;

        if (imageHasChanged) {
            const newImageUrl = changes.imageUrl?.currentValue;
            const newImageId = changes.imageId?.currentValue;
            // Only fall back to getImageUrl when imageId is actually defined — calling it with
            // undefined constructs a broken URL and immediately triggers an <img> error event.
            this.imageUrl = newImageUrl || (newImageId ? this.imageService.getImageUrl(newImageId, this.small ? 'medium' : 'original') : undefined);

            if (!this.imageUrl) {
                this.isError = true;
            }
        }
    }

    ngOnInit() {
        if (this.downloadable) {
            this.imageService.downloadRequest$.pipe(
                untilDestroyed(this),
            ).subscribe(req => {
                if ((this.imageUrl || this.imageId)
                    && (
                        (this.imageId && req.imageId === this.imageId)
                        || (this.imageUrl && req.imageUrl === this.imageUrl)
                    )
                ) {
                    this.downloadImage(req);
                }
            });
        }
    }

    loaded() {
        this.isLoaded = true;
        this.cdRef.markForCheck();
    }

    setError(errorEvent?: ErrorEvent) {
        console.error("Error loading image", errorEvent);
        this.isError = true;
        this.errorText = "Error loading image";
    }

    downloadImage(req: ImageDownloadRequest) {
        const localId = req.localScreenshotId;
        const id = req.imageId;
        const screenshotName = req.name;

        const downloadUrl$ = (() => {
            if (localId) {
                return this.imageService.getLocalScreenshot(localId);
            } else if (id) {
                return of(this.imageService.getImageUrl(id, undefined, true));
            } else {
                return of(req.imageUrl!);
            }
        })();

        downloadUrl$.subscribe((url: string) => {
            const a = document.createElement('a');
            a.href = url;
            a.target = "_blank";
            a.download = screenshotName;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        })
    }

    protected readonly faImage = faImage;

}
